import { c as _c } from "react/compiler-runtime";
import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { CmsImage } from '@/components/CmsImage';
import { SiteContainer } from '@/components/Layout/SiteContainer';
import { PrimaryTitle } from '@/components/PrimaryTitle';
import { mergeSx } from '@/lib/mergeSx';
import type { CmsBaseBlock } from '@/services/cms/blocks/BaseCmsBlock';
import type { CmsImageData } from '@/services/cms/models/CmsImageData';
export interface BlockContainerProps extends CmsBaseBlock {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  onClick?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>['onClick'];
}
function BlockTitle(t0) {
  const $ = _c(2);
  const {
    showTitle,
    title
  } = t0;
  if (!showTitle || !title) {
    return null;
  }
  let t1;
  if ($[0] !== title) {
    t1 = <PrimaryTitle>{title}</PrimaryTitle>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
type BlockBackgroundImageProps = {
  image?: CmsImageData;
};
function BlockBackgroundImage(t0) {
  const $ = _c(3);
  const {
    image
  } = t0;
  if (!image?.link) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      zIndex: -1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== image) {
    t2 = <CmsImage {...image} style={t1} />;
    $[1] = image;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function BlockContainer(t0) {
  const $ = _c(97);
  const {
    children,
    showContentContainer,
    narrow,
    sx: sxIn,
    showTitle,
    title,
    showBackground,
    showSeparatorBorder,
    onClick,
    id,
    className,
    verticalPadding,
    backgroundImage,
    variant
  } = t0;
  let t1;
  if ($[0] !== showBackground || $[1] !== showSeparatorBorder || $[2] !== sxIn || $[3] !== variant || $[4] !== verticalPadding) {
    t1 = mergeSx({
      position: "relative",
      ...(showBackground ? {
        bgcolor: "grey.100"
      } : undefined),
      ...(showSeparatorBorder ? {
        borderTopWidth: 28,
        borderTopStyle: "solid",
        borderTopColor: "primary.main"
      } : undefined),
      ...(verticalPadding ? {
        py: 9
      } : undefined),
      ...(variant === "Light" ? {
        color: "var(--mui-palette-text-primary)",
        "--mui-palette-primary-main": "#fff",
        "--mui-palette-text-secondary": "#fff",
        "--mui-palette-text-primary": "#fff",
        "--mui-palette-common-onBackgroundChannel": "#fff",
        "--mui-palette-action-disabledBackground": "var(--mui-palette-grey-600)",
        "--mui-palette-action-disabled": "var(--mui-palette-grey-200)"
      } : undefined)
    }, sxIn);
    $[0] = showBackground;
    $[1] = showSeparatorBorder;
    $[2] = sxIn;
    $[3] = variant;
    $[4] = verticalPadding;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const sx = t1;
  if (narrow) {
    if (backgroundImage) {
      const t2 = `BlockContainer ${className} narrow bg-img`;
      const t3 = `element-${id}`;
      let t4;
      if ($[6] !== backgroundImage) {
        t4 = <BlockBackgroundImage image={backgroundImage} />;
        $[6] = backgroundImage;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      let t5;
      if ($[8] !== showTitle || $[9] !== title) {
        t5 = <BlockTitle showTitle={showTitle} title={title} />;
        $[8] = showTitle;
        $[9] = title;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== children || $[12] !== t5) {
        t6 = <Container maxWidth="md">{t5}{children}</Container>;
        $[11] = children;
        $[12] = t5;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      let t7;
      if ($[14] !== className || $[15] !== onClick || $[16] !== sx || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t6) {
        t7 = <Box className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t4}{t6}</Box>;
        $[14] = className;
        $[15] = onClick;
        $[16] = sx;
        $[17] = t2;
        $[18] = t3;
        $[19] = t4;
        $[20] = t6;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      return t7;
    }
    if (showBackground) {
      const t2 = `BlockContainer ${className} narrow bg-solid`;
      const t3 = `element-${id}`;
      let t4;
      if ($[22] !== showTitle || $[23] !== title) {
        t4 = <BlockTitle showTitle={showTitle} title={title} />;
        $[22] = showTitle;
        $[23] = title;
        $[24] = t4;
      } else {
        t4 = $[24];
      }
      let t5;
      if ($[25] !== children || $[26] !== t4) {
        t5 = <Container maxWidth="md">{t4}{children}</Container>;
        $[25] = children;
        $[26] = t4;
        $[27] = t5;
      } else {
        t5 = $[27];
      }
      let t6;
      if ($[28] !== className || $[29] !== onClick || $[30] !== sx || $[31] !== t2 || $[32] !== t3 || $[33] !== t5) {
        t6 = <Box className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t5}</Box>;
        $[28] = className;
        $[29] = onClick;
        $[30] = sx;
        $[31] = t2;
        $[32] = t3;
        $[33] = t5;
        $[34] = t6;
      } else {
        t6 = $[34];
      }
      return t6;
    }
    const t2 = `BlockContainer ${className} narrow`;
    const t3 = `element-${id}`;
    let t4;
    if ($[35] !== showTitle || $[36] !== title) {
      t4 = <BlockTitle showTitle={showTitle} title={title} />;
      $[35] = showTitle;
      $[36] = title;
      $[37] = t4;
    } else {
      t4 = $[37];
    }
    let t5;
    if ($[38] !== children || $[39] !== className || $[40] !== onClick || $[41] !== sx || $[42] !== t2 || $[43] !== t3 || $[44] !== t4) {
      t5 = <Container className={t2} data-element-type={className} id={t3} maxWidth="md" sx={sx} onClick={onClick}>{t4}{children}</Container>;
      $[38] = children;
      $[39] = className;
      $[40] = onClick;
      $[41] = sx;
      $[42] = t2;
      $[43] = t3;
      $[44] = t4;
      $[45] = t5;
    } else {
      t5 = $[45];
    }
    return t5;
  }
  if (showContentContainer) {
    if (showBackground) {
      const t2 = `BlockContainer ${className} contained bg-solid`;
      const t3 = `element-${id}`;
      let t4;
      if ($[46] !== showTitle || $[47] !== title) {
        t4 = <BlockTitle showTitle={showTitle} title={title} />;
        $[46] = showTitle;
        $[47] = title;
        $[48] = t4;
      } else {
        t4 = $[48];
      }
      let t5;
      if ($[49] !== children || $[50] !== t4) {
        t5 = <SiteContainer>{t4}{children}</SiteContainer>;
        $[49] = children;
        $[50] = t4;
        $[51] = t5;
      } else {
        t5 = $[51];
      }
      let t6;
      if ($[52] !== className || $[53] !== onClick || $[54] !== sx || $[55] !== t2 || $[56] !== t3 || $[57] !== t5) {
        t6 = <Box className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t5}</Box>;
        $[52] = className;
        $[53] = onClick;
        $[54] = sx;
        $[55] = t2;
        $[56] = t3;
        $[57] = t5;
        $[58] = t6;
      } else {
        t6 = $[58];
      }
      return t6;
    }
    if (backgroundImage) {
      const t2 = `BlockContainer ${className} contained bg-img`;
      const t3 = `element-${id}`;
      let t4;
      if ($[59] !== backgroundImage) {
        t4 = <BlockBackgroundImage image={backgroundImage} />;
        $[59] = backgroundImage;
        $[60] = t4;
      } else {
        t4 = $[60];
      }
      let t5;
      if ($[61] !== showTitle || $[62] !== title) {
        t5 = <BlockTitle showTitle={showTitle} title={title} />;
        $[61] = showTitle;
        $[62] = title;
        $[63] = t5;
      } else {
        t5 = $[63];
      }
      let t6;
      if ($[64] !== children || $[65] !== t5) {
        t6 = <SiteContainer>{t5}{children}</SiteContainer>;
        $[64] = children;
        $[65] = t5;
        $[66] = t6;
      } else {
        t6 = $[66];
      }
      let t7;
      if ($[67] !== className || $[68] !== onClick || $[69] !== sx || $[70] !== t2 || $[71] !== t3 || $[72] !== t4 || $[73] !== t6) {
        t7 = <Box className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t4}{t6}</Box>;
        $[67] = className;
        $[68] = onClick;
        $[69] = sx;
        $[70] = t2;
        $[71] = t3;
        $[72] = t4;
        $[73] = t6;
        $[74] = t7;
      } else {
        t7 = $[74];
      }
      return t7;
    }
    const t2 = `BlockContainer ${className} contained`;
    const t3 = `element-${id}`;
    let t4;
    if ($[75] !== showTitle || $[76] !== title) {
      t4 = <BlockTitle showTitle={showTitle} title={title} />;
      $[75] = showTitle;
      $[76] = title;
      $[77] = t4;
    } else {
      t4 = $[77];
    }
    let t5;
    if ($[78] !== children || $[79] !== className || $[80] !== onClick || $[81] !== sx || $[82] !== t2 || $[83] !== t3 || $[84] !== t4) {
      t5 = <SiteContainer className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t4}{children}</SiteContainer>;
      $[78] = children;
      $[79] = className;
      $[80] = onClick;
      $[81] = sx;
      $[82] = t2;
      $[83] = t3;
      $[84] = t4;
      $[85] = t5;
    } else {
      t5 = $[85];
    }
    return t5;
  }
  const t2 = `BlockContainer ${className}`;
  const t3 = `element-${id}`;
  let t4;
  if ($[86] !== showTitle || $[87] !== title) {
    t4 = <BlockTitle showTitle={showTitle} title={title} />;
    $[86] = showTitle;
    $[87] = title;
    $[88] = t4;
  } else {
    t4 = $[88];
  }
  let t5;
  if ($[89] !== children || $[90] !== className || $[91] !== onClick || $[92] !== sx || $[93] !== t2 || $[94] !== t3 || $[95] !== t4) {
    t5 = <Box className={t2} data-element-type={className} id={t3} sx={sx} onClick={onClick}>{t4}{children}</Box>;
    $[89] = children;
    $[90] = className;
    $[91] = onClick;
    $[92] = sx;
    $[93] = t2;
    $[94] = t3;
    $[95] = t4;
    $[96] = t5;
  } else {
    t5 = $[96];
  }
  return t5;
}